import React, { useMemo } from "react"
import PropTypes from "prop-types"
import { Formik, Form } from "formik"
import * as yup from "yup"
import { Modal, ModalBody } from "reactstrap"
import { Bounce, toast } from "react-toastify"
import CustomButton from "../OnboardingComponents/CustomButton"
import CustomInput from "../OnboardingComponents/CustomInput"
import RoleSelect from "../OnboardingComponents/RoleSelect"
import { post } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { toastApiError } from "../../helpers/apiError"

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const buildValidationSchema = ({ requireDescription }) =>
  yup.object().shape({
    role: yup
      .object()
      .shape({
        id: yup.string().required("Role is required"),
        name: yup.string().required("Role name is required"),
      })
      .nullable()
      .required("Role is required"),
    hours: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
          ? undefined
          : Number(originalValue)
      )
      .typeError("Hours must be a number")
      .min(0.01, "Hours must be greater than 0")
      .max(168, "Hours cannot exceed 168")
      .required("Hours are required"),
    hourUnit: yup.string().oneOf(["day", "week"]).required(),
    description: requireDescription
      ? yup
          .string()
          .trim()
          .required("Description is required")
          .max(500, "Description must be less than 500 characters")
      : yup
          .string()
          .trim()
          .max(500, "Description must be less than 500 characters"),
  })

const EMPTY_VALUES = {
  role: null,
  hours: "",
  hourUnit: "week",
  description: "",
}

const resolveInitialValues = invite => {
  if (!invite) return EMPTY_VALUES

  const roleId = invite.roleId || invite.role_type_id || invite.roleType?.id
  const roleName =
    invite.roleName ||
    invite.roleType?.public_name ||
    invite.roleType?.name ||
    invite.role ||
    ""
  const cadence = String(
    invite.hoursCadence || invite.hours_cadence || "per_week"
  ).toLowerCase()
  const hours =
    invite.estimatedHours ?? invite.estimated_hours ?? invite.hours ?? ""

  return {
    role: roleId ? { id: String(roleId), name: String(roleName || "Role") } : null,
    hours: hours === "" || hours == null ? "" : Number(hours),
    hourUnit: cadence.includes("day") ? "day" : "week",
    description: String(
      invite.description ?? invite.memberDescription ?? ""
    ).trim(),
  }
}

/**
 * AcceptInviteModal — collect role, hours, and description when accepting
 * a project invitation, or edit the same fields for an existing member.
 */
const AcceptInviteModal = ({
  isOpen,
  onClose,
  invite = null,
  onSuccess,
  isMandatory = false,
  useProjectMemberAccept = false,
  mode = "accept",
}) => {
  const isEdit = mode === "edit"
  const inviteId = invite?.id || invite?.memberId
  const projectId = invite?.projectId
  const initialValues = useMemo(
    () => (isEdit ? resolveInitialValues(invite) : EMPTY_VALUES),
    [invite, isEdit]
  )
  const validationSchema = useMemo(
    () => buildValidationSchema({ requireDescription: !isEdit }),
    [isEdit]
  )

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    if (!inviteId) {
      setSubmitting(false)
      return
    }

    if ((useProjectMemberAccept || isEdit) && !projectId) {
      toast.error("Project information is missing.", TOAST_OPTIONS)
      setSubmitting(false)
      return
    }

    try {
      const basePayload = {
        role_type_ids: values.role?.id ? [values.role.id] : [],
        estimated_hours: Number(values.hours) || 0,
        hours_cadence: values.hourUnit === "day" ? "per_day" : "per_week",
        description: String(values.description || "").trim(),
      }

      let response
      if (isEdit) {
        response = await post(
          endpoints.updateProjectMember(projectId, inviteId),
          basePayload,
          { skipErrorToast: true }
        )
      } else {
        const url = useProjectMemberAccept
          ? endpoints.acceptProjectMember(projectId, inviteId)
          : endpoints.respondProjectInvitation(inviteId)
        const payload = useProjectMemberAccept
          ? basePayload
          : { action: "accept", ...basePayload }
        response = await post(url, payload, { skipErrorToast: true })
      }

      if (!response?.data?.success) {
        toastApiError(
          response?.data || response,
          isEdit ? "Failed to update collaborator" : "Failed to accept invite"
        )
        setSubmitting(false)
        return
      }

      toast.success(
        response?.data?.message ||
          (isEdit
            ? "Collaborator updated successfully."
            : "Invite accepted successfully."),
        TOAST_OPTIONS
      )
      resetForm()
      if (typeof onSuccess === "function") await onSuccess()
      onClose()
    } catch (err) {
      toastApiError(
        err,
        isEdit ? "Failed to update collaborator" : "Failed to accept invite"
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isMandatory) onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={isMandatory ? () => {} : handleClose}
      keyboard={!isMandatory}
      backdrop={isMandatory ? "static" : true}
      centered
      className="amok-modal amok-modal--invite"
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body invites-modal__body">
        <Formik
          key={
            isOpen
              ? `${isEdit ? "edit" : "accept"}-invite-${inviteId || "new"}`
              : "closed"
          }
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnBlur
          validateOnChange
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldValue,
            setFieldTouched,
            isSubmitting,
          }) => (
            <Form className="invites-modal">
              <div className="invites-modal__main">
                <div className="invites-modal__header">
                  <h2 className="invites-modal__title">
                    {isEdit ? "Edit Collaborator" : "Accept Invitation"}
                  </h2>
                  <p className="invites-modal__subtitle">
                    {isEdit
                      ? "Update role, estimated hours, and responsibilities for this collaborator."
                      : "Confirm your role, estimated hours, and responsibilities to join this project."}
                  </p>
                </div>

                <div className="invites-modal__fields">
                  <RoleSelect
                    label="Select Role"
                    placeholder="Select a role"
                    value={values.role}
                    onChange={nextRole => {
                      setFieldValue("role", nextRole)
                      setFieldTouched("role", true, false)
                    }}
                    onBlur={() => setFieldTouched("role", true)}
                    error={
                      touched.role && typeof errors.role === "string"
                        ? errors.role
                        : null
                    }
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                    required
                  />

                  <div className="amok-field invites-modal__hours-field">
                    <label className="amok-field__label" htmlFor="accept-hours">
                      Estimated Hours
                    </label>
                    <div
                      className={`invites-modal__hours-input${
                        touched.hours && errors.hours
                          ? " invites-modal__hours-input--error"
                          : ""
                      }`}
                    >
                      <input
                        id="accept-hours"
                        type="number"
                        min="0"
                        step="any"
                        inputMode="decimal"
                        className="amok-field__input invites-modal__hours-number"
                        placeholder="0"
                        name="hours"
                        value={values.hours}
                        onChange={handleChange}
                        onBlur={() => setFieldTouched("hours", true)}
                      />
                      <div
                        className="invites-modal__unit-toggle"
                        role="group"
                        aria-label="Hours unit"
                      >
                        <button
                          type="button"
                          className={`invites-modal__unit-pill${
                            values.hourUnit === "day"
                              ? " invites-modal__unit-pill--active"
                              : ""
                          }`}
                          onClick={() => setFieldValue("hourUnit", "day")}
                        >
                          Per Day
                        </button>
                        <button
                          type="button"
                          className={`invites-modal__unit-pill${
                            values.hourUnit === "week"
                              ? " invites-modal__unit-pill--active"
                              : ""
                          }`}
                          onClick={() => setFieldValue("hourUnit", "week")}
                        >
                          Per Week
                        </button>
                      </div>
                    </div>
                    {touched.hours && errors.hours ? (
                      <span className="amok-field__message amok-field__message--error">
                        {errors.hours}
                      </span>
                    ) : null}
                  </div>

                  <CustomInput
                    label="Description"
                    name="description"
                    textarea
                    rows={3}
                    placeholder="e.g. Handling motion graphics"
                    value={values.description}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched("description", true)}
                    error={touched.description && errors.description}
                    required={!isEdit}
                  />
                </div>
              </div>

              <div className="invites-modal__actions">
                {!isMandatory && (
                  <CustomButton
                    variant="danger"
                    size="md"
                    type="button"
                    className="invites-modal__cancel"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </CustomButton>
                )}
                <CustomButton
                  variant="primary"
                  size="md"
                  type="submit"
                  className="invites-modal__submit"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  {isEdit ? "Save Changes" : "Accept Invite"}
                </CustomButton>
              </div>
            </Form>
          )}
        </Formik>
      </ModalBody>
    </Modal>
  )
}

AcceptInviteModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  invite: PropTypes.shape({
    id: PropTypes.string,
    memberId: PropTypes.string,
    projectId: PropTypes.string,
    name: PropTypes.string,
    roleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    roleName: PropTypes.string,
    estimatedHours: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    hoursCadence: PropTypes.string,
    description: PropTypes.string,
  }),
  onSuccess: PropTypes.func,
  isMandatory: PropTypes.bool,
  useProjectMemberAccept: PropTypes.bool,
  mode: PropTypes.oneOf(["accept", "edit"]),
}

AcceptInviteModal.defaultProps = {
  mode: "accept",
}

export default AcceptInviteModal
